import type { ContratProDTO } from '@/features/api/extranet/dto/Demande/CPRO'
import { TypesContextesAutorisesEnum, type TypeContextCourant } from '@/features/auth/context-app'
import { PropertyStatus, type PropertyStatusConfiguration } from '@/features/domaine/property-status'
import type { ContextReglesCpro } from './types'

function selonTypeContextCourant(
  typeContext: TypeContextCourant,
  conditionVrai: PropertyStatus,
  conditionFaux: PropertyStatus,
) {
  return function (_value: any, _parent: any, _modele: ContratProDTO, context: ContextReglesCpro) {
    if (typeContext === TypesContextesAutorisesEnum.Adherent) {
      return context.typeContext === TypesContextesAutorisesEnum.Adherent ? conditionVrai : conditionFaux
    } else {
      return context.typeContext === TypesContextesAutorisesEnum.Prestataire ? conditionVrai : conditionFaux
    }
  }
}

const optionnelPrestataire = selonTypeContextCourant(
  TypesContextesAutorisesEnum.Adherent,
  PropertyStatus.Required,
  PropertyStatus.Optional,
)

const requiredOrNotPresentPrestataire = selonTypeContextCourant(
  TypesContextesAutorisesEnum.Adherent,
  PropertyStatus.Required,
  PropertyStatus.NotPresent,
)

export const propertyStatusConfiguration: PropertyStatusConfiguration<ContratProDTO, ContextReglesCpro> = {
  sousDispositif: PropertyStatus.Required,
  lieuSignature: optionnelPrestataire,
  attestationPiecesJustificatives: optionnelPrestataire,
  employeur: {
    contact: {
      email: PropertyStatus.Required,
    },
    effectifTotalEntreprise: requiredOrNotPresentPrestataire,
    caisseRetraitreComplementaire: optionnelPrestataire,
    organismePrevoyance: optionnelPrestataire,
    /*
    numeroProjet: (_value, _parent, model) =>
      model.employeur?.etablissement?.naf?.code === nafAutoriseTT ? PropertyStatus.Optional : PropertyStatus.NotPresent,
    */
  },
  salarie: {
    nomNaissance: PropertyStatus.Required,
    prenom: PropertyStatus.Required,
    sexe: optionnelPrestataire,
    dateNaissance: optionnelPrestataire,
    nirTemporaire: optionnelPrestataire,
    nir: optionnelPrestataire,
    departementNaissance: optionnelPrestataire,
    communeNaissance: optionnelPrestataire,
    beneficieReconnaissanceTravailleurHandicape: optionnelPrestataire,
    adresse: {
      voie: optionnelPrestataire,
      codePostal: optionnelPrestataire,
      commune: optionnelPrestataire,
    },
    email: optionnelPrestataire,
    telephone: optionnelPrestataire,
    csp: optionnelPrestataire,
    diplomeOuTitreLePlusEleveObtenu: optionnelPrestataire,
    situationAvantContrat: optionnelPrestataire,

    inscritPoleEmploi: optionnelPrestataire,
    numeroInscriptionPoleEmploi: (_value, parent, _model, _context) => {
      if (parent.inscritPoleEmploi === true) {
        return optionnelPrestataire(_value, parent, _model, _context)
      }
      return PropertyStatus.NotPresent
    },
    dureeMoisPoleEmploi: (_value, parent, _model, _context) => {
      if (parent.inscritPoleEmploi === true) {
        return optionnelPrestataire(_value, parent, _model, _context)
      }
      return PropertyStatus.NotPresent
    },
    equivalenceJeune: (value, parent, globalModel, context) =>
      parent?.beneficieReconnaissanceTravailleurHandicape === false
        ? optionnelPrestataire(value, parent, globalModel, context)
        : PropertyStatus.NotPresent,
    extensionBOE: (value, parent, globalModel, context) =>
      parent?.beneficieReconnaissanceTravailleurHandicape === false
        ? optionnelPrestataire(value, parent, globalModel, context)
        : PropertyStatus.NotPresent,
  },
  tutorat: {
    tuteurEntrepriseEmployeur: {
      nom: optionnelPrestataire,
      prenom: optionnelPrestataire,
      dateNaissance: optionnelPrestataire,
      emploiOccupe: optionnelPrestataire,
    },
    attestationTuteurCriteresEligibilite: optionnelPrestataire,
    attestationTuteurTroisStagiaires: optionnelPrestataire,
    nafEntrepriseUtilisatrice: {
      code: optionnelPrestataire,
    },
  },
  contrat: {
    natureContrat: optionnelPrestataire,
    typeContratAvenant: optionnelPrestataire,
    emploiOccupe: optionnelPrestataire,
    niveauQualificationCcn: optionnelPrestataire,
    coefficientCcn: optionnelPrestataire,
    dateDebutExecutionContrat: optionnelPrestataire,
    dateFinCddOuActionSiCdi: optionnelPrestataire,
    dureePeriodeEssaiJours: optionnelPrestataire,
    dureeHebdomadaire: {
      heure: optionnelPrestataire,
      minute: optionnelPrestataire,
    },
    dateConclusion: optionnelPrestataire,
    dateEffetAvenant: (_value, parent, _model, context) =>
      context.typeContext === TypesContextesAutorisesEnum.Adherent && parent.typeContratAvenant === 'Avenant'
        ? PropertyStatus.Required
        : PropertyStatus.Optional,
    salaire: {
      modeRemuneration: optionnelPrestataire,
      montant: requiredOrNotPresentPrestataire,
    },
  },
  formation: {
    intitule: PropertyStatus.Required,
    typeQualification: PropertyStatus.Required,
    typeReferentielFormation: (_value, parent) =>
      parent.typeQualification === 'Cqp' ? PropertyStatus.Required : PropertyStatus.NotPresent,
    rncp: {
      code: (_value, _parent, model) => {
        if (!model.sousDispositif) return PropertyStatus.NotPresent
        if (model.formation?.typeQualification === 'Rncp') {
          return PropertyStatus.Required
        } else if (model.formation?.typeQualification === 'Cqp') {
          return model.formation?.typeReferentielFormation === 'Rncp'
            ? PropertyStatus.Required
            : PropertyStatus.NotPresent
        }
        return PropertyStatus.NotPresent
      },
    },
    formacode: {
      code: (_value, _parent, model) => {
        if (!model.sousDispositif) return PropertyStatus.NotPresent
        if (model.formation?.typeQualification === 'Cqp') {
          return model.formation?.typeReferentielFormation === 'Formacode'
            ? PropertyStatus.Required
            : PropertyStatus.NotPresent
        }
        return PropertyStatus.NotPresent
      },
    },
    nsf: {
      code: PropertyStatus.Required,
    },

    diplomeOuTitreVise: PropertyStatus.Required,
    dureeTotale: PropertyStatus.Required,
    dureeTotaleEnseignement: PropertyStatus.Required,
    modules: {
      etablissementPrestataire: {
        siret: {
          valeur: PropertyStatus.Required,
          siren: PropertyStatus.Required,
          nic: PropertyStatus.Required,
        },
      },
      modalitePedagogique: PropertyStatus.Required,
      dureeFoad: (_value, _index, parent, _model, _context) => {
        return parent.modalitePedagogique === 'Mixte' ? PropertyStatus.Required : PropertyStatus.NotPresent
      },
      dateDebut: PropertyStatus.Required,
      dateFin: PropertyStatus.Required,
      duree: PropertyStatus.Required,

      avecSubrogation: (_value, _index, parent, _model, _context) => {
        return parent.typeModule === 'Interne' ? PropertyStatus.NotPresent : PropertyStatus.Required
      },
      attestationMoyenRealisation: (_value, _index, parent, _model, _context) => {
        return parent.typeModule === 'Interne' ? PropertyStatus.Required : PropertyStatus.NotPresent
      },

      fraisPedagogiques: {
        // montantCoutPedagogiques: PropertyStatus.Required,
        montant: (_value, _index, parent, model, _context) => {
          if (parent.nature! === 'LivrableOfa' && model.sousDispositif !== 'Prodiat') return PropertyStatus.NotPresent

          // On récupère les infos du module
          const module = model.formation?.modules
            ?.filter((x) => (x.fraisPedagogiques?.filter((y) => y === parent).length ?? 0) > 0)
            .shift()

          if (
            module?.typeModule === 'Externe' &&
            model.sousDispositif === 'Prodiat' &&
            parent.nature! === 'Pedagogique'
          )
            return PropertyStatus.Optional
          return parent.nature! === 'Pedagogique' ? PropertyStatus.Required : PropertyStatus.Optional
        },
      },
      fraisAnnexesSalarie: {
        montant: (_value, _index, parent, _model, _context) => {
          if (typeof parent.quantite !== 'number') return PropertyStatus.Optional
          else return PropertyStatus.Required
        },
        quantite: (_value, _index, parent, _model, _context) => {
          if (parent.baseCalcul !== 'Quantite') return PropertyStatus.NotPresent
          if (typeof parent.montant !== 'number') return PropertyStatus.Optional
          return PropertyStatus.Required
        },
      },
    },
  },

  consentementTraitementDonnees: PropertyStatus.Required,
} as const
